import { forwardRef, useState, useEffect, useRef, useImperativeHandle } from "react";
import ModelIndex, { MessagesTypeProps, ModelIndexHandle } from "@components/layout/ModelIndex";
import styles from "./style.module.scss";
import moment from "moment";
import { formatMoneyValue } from "@utils/Currency";
import { CreditnoteApi, CreditnoteDataType } from "@/api/CreditnoteApi";
import { OpenAssignCreditnotePubSub, OpenSendAtvDocumentPubSub } from "@utils/pubsubs/PubSub";
import { useAppSelector } from "@/store/hooks";
import { sendECreditnoteData } from "@utils/EDocument";
import {
  buildAutoReferenceFromInvoice,
  mergeReferences,
  serializeStoredReferences,
  toInfoRefElements,
} from "@utils/DocumentReferences";
import { cancelAcceptedNoteWithCounter } from "@utils/noteCancellation";
import { showAlert } from "@utils/Alert";
import { faEnvelope, faEye, faFileExcel, faFileExport, faPaperPlane, faPrint, faXmark } from "@fortawesome/free-solid-svg-icons";
import LoadingScreen from "@components/content/LoadingScreen";
import { renderEDocumentStatusOffCanvasTrigger } from "@utils/ModelIndexUtils";
import { EasyposEInvoice } from "@/atv/submitInvoice";
import { EDOCUMENT_SUBMITION_STATUS_CODE, EDOCUMENT_SUBMITION_STATUS_SENT } from "@/atv/eInvoiceApi";
import SendAtvDocumentToEmailOffCanvas from "@components/layout/Offcanvas/SendAtvDocumentToEmailOffCanvas";
import { CreditnoteFormDataKeys } from "@/generated/CreditnoteFormDataKeys";
import { CreditnoteRelatedAttributes } from "@/formTypes/CreditnoteDataType";
import AssignCreditnoteOffCanvas from "@components/layout/Offcanvas/AssignCreditnoteOffCanvas";
import { generateAndUploadPdf } from "@utils/htmlExport";
import { documentPdfS3Key, documentPrintPath } from "@utils/documentPdf";

type CreditnoteModelIndexProps = {
  messages?: MessagesTypeProps;
  mandatoryFilters?: Array<Record<string, unknown>>;
  fetchData?: (...args: Parameters<typeof CreditnoteApi.fetchAll>) => ReturnType<typeof CreditnoteApi.fetchAll>;
};

const CreditnoteModelIndex = forwardRef<ModelIndexHandle, CreditnoteModelIndexProps>(
  ({ messages, mandatoryFilters, fetchData }, ref) => {

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const suscriptionData = useAppSelector(state => state.suscription.suscription)
    const resolvedMessages = {
      noResults: "No hay notas de crédito creadas aún",
      ...messages,
    };

    const resolvedMandatoryFilters = mandatoryFilters ?? [];
    const resolvedFetch = fetchData ?? ((...args) => CreditnoteApi.fetchAll(...args));
    const [easyposEInvoice, setEasyposEInvoice] = useState<EasyposEInvoice>()
    const internalRef = useRef<ModelIndexHandle>(null);
    useImperativeHandle(ref, () => internalRef.current as ModelIndexHandle, []);

    useEffect(() => {
      if (suscriptionData) {
        setEasyposEInvoice(new EasyposEInvoice(suscriptionData))
      }
    }, [suscriptionData]);

    const reFetch = () => {
      internalRef.current?.refetch()
    }

    const triggerEmailSend = (item: CreditnoteDataType) => {
      OpenSendAtvDocumentPubSub.publish(
        {
          documentId: item.id,
          openOffCanvas: true,
          documentKind: "Creditnote",
          suscription_numero: Number(suscriptionData?.numero || 0),
          documentkey: `${item.document_key}`,
          clientEmail: `${item.email || item.invoice?.email || ""}`,
          emailBody: `${suscriptionData?.invoice_email}`,
          emailSubject: `${suscriptionData?.nombre} -- Nota de Credito #${item.numero}`,
          Suscription: {
            email: `${suscriptionData?.email}`
          }
        })
    }

    const cancelCreditnote = async (item: CreditnoteDataType) => {
      const creditnote = await CreditnoteApi.get(`${item.id}`, {
        selectionSet: [
          ...CreditnoteFormDataKeys.filter(k => !CreditnoteRelatedAttributes.includes(k)),
          "currency.*",
          "invoice.*",
          "client.*",
          "creditnoteProducts.*",
          "creditnoteProducts.product.*",
        ]
      })

      try {
        setIsLoading(true)
        const result = await cancelAcceptedNoteWithCounter({
          note: creditnote,
          kind: "Creditnote",
          reason: `Anulacion de nota de credito ${item.document_key}`,
          touchStock: true,
          easyposEInvoice,
          einvoiceActive: Boolean(suscriptionData?.einvoice_status),
        })

        showAlert("success", `Se ha creado correctamente la nota de Debito #${result.counterNumero} para la cancelacion de la nota de credito #${item.numero} `);
      } catch (err) {
        showAlert("danger", (err as Error).message);
      } finally {
        setIsLoading(false);
      }
    }

    const resendENote = async (noteId: string) => {
      setIsLoading(true);
      try {
        if (easyposEInvoice) {
          const creditnoteData = await CreditnoteApi.get(noteId, {
            selectionSet: [
              ...CreditnoteFormDataKeys.filter(k => !CreditnoteRelatedAttributes.includes(k)),
              "currency.*",
              "invoice.*",
              "client.*",
              "creditnoteProducts.*",
              "creditnoteProducts.product.*",
            ]
          })
          const autoRef = buildAutoReferenceFromInvoice(
            creditnoteData.invoice,
            "notaDeCredito",
            `${creditnoteData.description}`,
          )
          const mergedRefs = mergeReferences([autoRef], [])
          await CreditnoteApi.update(noteId, {
            info_references: serializeStoredReferences(mergedRefs),
          })
          // Regenerar antes de reenviar: si el PDF no se genero al emitir, este es el
          // unico camino que puede dejarlo en S3 antes de que salga el correo.
          await generateAndUploadPdf(
            documentPrintPath("Creditnote", noteId),
            documentPdfS3Key("Creditnote", suscriptionData?.numero, `${creditnoteData.document_key}`),
          )
          const response = await sendECreditnoteData(easyposEInvoice, noteId, toInfoRefElements(mergedRefs))
          if (response.status == EDOCUMENT_SUBMITION_STATUS_CODE[EDOCUMENT_SUBMITION_STATUS_SENT]) {
            // El reenvio manual gana: si la nota estaba esperando a que se registrara su
            // factura, hay que soltar ese payload o submit-pending-enote la enviaria de
            // nuevo y Hacienda rechazaria la clave duplicada.
            if (creditnoteData.pending_submission) {
              await CreditnoteApi.update(noteId, { pending_submission: null })
            }
            showAlert("success", "La Nota de Credito ha sido reenviada correctamente, el estado de la nota de Credito se actualizara una vez Tributacion de una respuesta sobre el documento")
          } else {
            showAlert("danger", "La Nota de Credito no ha sido reenviada correctamente")
          }
        } else {
          showAlert("danger", "No fue possible enviar el document a hacienda ")
        }
      } finally {
        setIsLoading(false)
      }
    }

    return (
      <>
        <ModelIndex<CreditnoteDataType>
          ref={internalRef}
          meiliConfig={{
            index: "creditnotes",
            sort: ["created:desc"]
          }}
          messages={resolvedMessages}
          mandatoryFilters={resolvedMandatoryFilters}
          fetchData={resolvedFetch}
          filterProps={[
            { key: "sequence", filterType: "contains" },
            { key: "document_key", filterType: "contains" },
            { key: "numero", filterType: "eq", type: "number" },
            { key: "invoice", innerProp: "numero", filterType: "eq", type: "number" },
            { key: "client", innerProp: "nombre", filterType: "eq", type: "number" },
            { key: "invoice", innerProp: "nombre_factura", filterType: "contains", },
          ]}
          primaryProperties={[
            { key: "sequence", highlighted: true, className: styles.sequence },
            {
              key: (d) => {
                if ("invoiceNombreFactura" in d) {
                  return (d as any).invoiceNombreFactura
                }
                return d.client?.nombre

              }, alignment: "Start"
            },
          ]}
          primaryPropertiesSizing={[
            { sm: "4", xs: "12" },
            { sm: "8", xs: "12" },
          ]}
          secondaryProperties={[
            {
              key: (data) => moment(data.created, "YYYY-MM-DD").format("DD-MM-YYYY"),
              title: "Fecha",
            },
            {
              key: (data) => renderEDocumentStatusOffCanvasTrigger(data, { model: "Creditnote", id: `${data.id}` }),
              title: "Estado ATV"
            },
            {
              key: (data) => {
                if ("currency_id" in data) {
                  return formatMoneyValue(data.total, (data as any).currency_id)
                }
                formatMoneyValue(data.total, data.currency.currency_id)
              },
              title: "Total",
              highlighted: true,
            },
          ]}
          secondaryPropertiesSizing={[
            { sm: "4", md: "2" },
            { sm: "4", md: "3" },
            { sm: "4", md: "2" },
          ]}
          listActions={[
            {
              isVisible: (data) => !!data.need_assignment && !!data.appliedInvoiceId,
              onClick: (id, item) => { window.open(`/user/invoices/view/${item.appliedInvoiceId}`, '_blank') },
              actionTitle: "Ver Factura asignada",
              iconProps: { icon: faFileExport }
            },
            {
              isVisible: (data) => !!data.need_assignment && !data.appliedInvoiceId,
              onClick: (id, item) => OpenAssignCreditnotePubSub.publish({
                openOffCanvas: true,
                creditNote: item
              }),
              actionTitle: "Asignar Nota de Credito a Factura",
              iconProps: { icon: faFileExcel }
            },
            {
              isVisible: (data) => !data.edocument_status_payload,
              onClick: (id, item) => resendENote(id),
              actionTitle: "Reenviar Nota de Credito al ATV",
              iconProps: { icon: faPaperPlane }
            },
            {
              onClick: (id, item) => cancelCreditnote(item),
              actionTitle: "Anular Nota de Credito",
              iconProps: { icon: faXmark, className: styles.cancelBtn },
              needConfirm: true,
              confirmProps: {
                confirmMessage: "Esta seguro que desea anular esta nota de Credito? Esta accion creara una nota de Debito",
              }
            },
            {
              onClick: (id, item) => triggerEmailSend(item),
              actionTitle: "Enviar Por Correo ",
              iconProps: { icon: faEnvelope }
            },
            {
              href: "/user/creditnotes/view/{:id}",
              actionTitle: "Ver Nota de Credito",
              target: "_blank",
              iconProps: { icon: faEye }
            },
            {
              href: "/user/creditnotes/print/{:id}/0/1",
              actionTitle: "Imprimir Nota de Credito",
              target: "_blank",
              iconProps: { icon: faPrint }
            }
          ]}
        />
        <SendAtvDocumentToEmailOffCanvas
          title="Envio de Nota de Credito a Email" />
        <AssignCreditnoteOffCanvas
          onAssignmentDone={() => reFetch()}
        />

        <LoadingScreen isLoading={isLoading} />
      </>
    );
  }
);

CreditnoteModelIndex.displayName = "CreditnoteModelIndex";
export default CreditnoteModelIndex;